import React, { useState, useEffect, useRef, useContext } from 'react';
import { Modal, Button, Form, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AuthContext } from '../../../../../../AuthContext/AuthContext';
import { baseURL } from '../../../../../../Apiservices/Api';
import { adminMail } from '../../../../../../Apiservices/Api';

const EmailCommentModalPopemployee = ({ 
  show, 
  onHide, 
  selectedLead, 
  userName,
  userEmail 
}) => {
  const [activeTab, setActiveTab] = useState('comments');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [managerid, setManagerId] = useState(null);
  const [assignedSalesId, setAssignedSalesId] = useState(null);
  
  // Email states
  const [emailHistory, setEmailHistory] = useState([]);
  const [groupedEmailHistory, setGroupedEmailHistory] = useState([]);
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [receiverEmails, setReceiverEmails] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [bccEmails, setBccEmails] = useState("");
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);
  const chatEndRef = useRef(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setComments([]);
      setNewComment("");
      setIsAddingComment(false);
      setIsLoadingComments(false);
      setManagerId(null);
      setAssignedSalesId(null);
      setEmailHistory([]);
      setGroupedEmailHistory([]);
      setText("");
      setSubject("");
      setSelectedFile(null);
      setReceiverEmails("");
      setCcEmails("");
      setBccEmails("");
      setShowCC(false);
      setShowBCC(false);
      setActiveTab('comments');
    }
  }, [show]);

  // Fetch data when modal opens
  useEffect(() => {
    if (selectedLead && show) {
      setManagerId(selectedLead.managerid);
      setAssignedSalesId(selectedLead.assignedSalesId);
      setReceiverEmails(selectedLead.email || "");
      
      if (activeTab === 'comments') {
        fetchComments(selectedLead.leadid);
      } else if (activeTab === 'emails') {
        fetchEmailHistory(selectedLead.leadid);
      }
    }
  }, [selectedLead, show, activeTab]);

  // Scroll to bottom when email history updates
  useEffect(() => {
    if (activeTab === 'emails') {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [groupedEmailHistory, activeTab]);

  const fetchComments = async (leadid) => {
    if (!leadid) return;
    
    setIsLoadingComments(true);
    try {
      const response = await axios.get(`${baseURL}/comments/${leadid}`);
      const sortedComments = response.data.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      setComments(sortedComments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const fetchEmailHistory = async (leadid) => {
    try {
      if (!leadid) {
        console.error("Lead ID is required");
        return;
      }
  
      const response = await axios.get(`${baseURL}/api/email-history`, {
        params: { leadid }
      });
  
      const sortedEmails = response.data.sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
  
      const groupedEmails = [];
      let currentGroup = null;
  
      sortedEmails.forEach((email) => {
        if (email.type === "sent") {
          if (currentGroup && 
              Math.abs(new Date(email.created_at) - new Date(currentGroup.created_at)) < 60000 &&
              email.subject === currentGroup.subject &&
              email.text === currentGroup.text) {
            
            currentGroup.recipients.push(email.receiver_email);
            if (new Date(email.created_at) < new Date(currentGroup.created_at)) {
              currentGroup.created_at = email.created_at;
            }
          } else {
            if (currentGroup) groupedEmails.push(currentGroup);
            currentGroup = {
              ...email,
              recipients: [email.receiver_email],
              isGrouped: true,
              id: email.id
            };
          }
        } else {
          if (currentGroup) {
            groupedEmails.push(currentGroup);
            currentGroup = null;
          }
          groupedEmails.push({
            ...email,
            isGrouped: false
          });
        }
      });
  
      if (currentGroup) groupedEmails.push(currentGroup);
  
      setGroupedEmailHistory(groupedEmails);
    } catch (error) {
      console.error("Error fetching email history:", error);
    }
  };

  const handleAddComment = async () => {
    if (!selectedLead) {
      console.error("No lead selected");
      return;
    }
    
    if (!newComment.trim()) return;
    
    setIsAddingComment(true);
    const trimmedComment = newComment.trim();
    
const commentName = userName ? userName : "Sales";
    const comment = {
      name: commentName,
      leadid: selectedLead.leadid,
      timestamp: new Date().toISOString(),
      text: trimmedComment,
      notificationmessage: `${commentName}: ${trimmedComment}`,
      managerId: managerid,
      userId: assignedSalesId,
      email: adminMail
    };

    try {
      const commenturl = `${baseURL}/comments/add`;
      const response = await fetch(commenturl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(comment),
      });

      if (response.ok) {
        const addedComment = await response.json();
        setComments(prevComments => [addedComment, ...prevComments]);
        setNewComment("");
      } else {
        console.error("Failed to add comment");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.size > 2 * 1024 * 1024) {
      alert("File size must be below 2MB.");
      return;
    }
    setSelectedFile(file);
  };

  const handleSendEmail = async () => {
    if (!text && !selectedFile) {
      alert("Please enter a message or upload a file.");
      return;
    }

    if (!userEmail) {
      alert("Sender email not found. Please log in again.");
      return;
    }

    const receivers = receiverEmails.split(",").map(email => email.trim()).filter(Boolean);
    if (receivers.length === 0) {
      alert("Please enter at least one recipient email.");
      return;
    }

    const cc = showCC ? ccEmails.split(",").map(email => email.trim()).filter(Boolean) : [];
    const bcc = showBCC ? bccEmails.split(",").map(email => email.trim()).filter(Boolean) : [];

    setLoading(true);
    const formData = new FormData();
    formData.append("leadid", selectedLead.leadid);
    formData.append("sender_email", userEmail);
    formData.append("text", text);
    formData.append("subject", subject || "New Message");
    formData.append("type", "sent");
    formData.append("is_plain_text", "false");
    formData.append("receiver_emails", JSON.stringify(receivers));
    if (showCC) formData.append("cc_emails", JSON.stringify(cc));
    if (showBCC) formData.append("bcc_emails", JSON.stringify(bcc));

    if (selectedFile) {
      formData.append("file", selectedFile);
    }

    try {
      await axios.post(`${baseURL}/api/send-bulk-emails`, formData);
      alert("Emails sent successfully!");
      setText("");
      setSubject("");
      setCcEmails("");
      setBccEmails("");
      setSelectedFile(null);
      fetchEmailHistory(selectedLead.leadid);
    } catch (error) {
      console.error("Error sending bulk emails:", error);
      alert("Failed to send bulk emails.");
    } finally {
      setLoading(false);
    }
  };

  const cleanEmailText = (text) => {
    if (!text) return "";
    const cleanedText = text.split(/\n?On\s.+\s(?:at|wrote:)/i)[0];
    return cleanedText.replace(/--\sThis email has been checked for viruses.*/is, "").trim();
  };

  if (!selectedLead) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Comments & Email History</Modal.Title>
      </Modal.Header>
      
      {/* Tabs placed outside Modal.Header but inside Modal */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mx-3 mt-2"
        style={{ borderBottom: '1px solid #dee2e6' }}
      >
        <Tab eventKey="comments" title="Comments" />
        <Tab eventKey="emails" title="Email History" />
      </Tabs>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {activeTab === 'comments' && (
          <div className="comment-form-container">
            <div className="mb-3">
              <Form.Group>
                <Form.Label>Add a New Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Write your comment here..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <Button
                  className="mt-2"
                  style={{ backgroundColor: "#ff9966", borderColor: "#ff9966" }}
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || isAddingComment}
                >
                  {isAddingComment ? 'Adding...' : 'Add Comment'}
                </Button>
              </Form.Group>
            </div>

            {/* Comments section with fixed height and scroll */}
            <div style={{ 
              height: "300px",
              overflowY: "auto",
              border: "1px solid #ddd", 
              padding: "10px", 
              borderRadius: "5px" 
            }}>
              {isLoadingComments ? (
                <p className="text-center text-muted">Loading comments...</p>
              ) : comments.length === 0 ? (
                <p className="text-center text-muted">No comments yet</p>
              ) : (
                comments.map((comment, index) => (
                  <div key={comment.id || index} className="mb-3">
                    <p style={{ fontSize: "12px", color: "gray", marginBottom: "2px" }}>
                      {new Date(comment.timestamp).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      })}
                    </p>
                    <p style={{ marginBottom: "5px" }}>
                      <strong>{comment.name}</strong>:  
                      <span style={{ whiteSpace: "pre-line", marginLeft: "5px" }}>{comment.text}</span>
                    </p>
                    <hr style={{ margin: "5px 0" }} />
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'emails' && (
          <div className="email-container">
            {/* Email History */}
            <div className="email-history-list" style={{ maxHeight: "300px", overflowY: "auto", marginBottom: "20px", border: "1px solid #ddd", padding: "10px", borderRadius: "5px" }}>
              {groupedEmailHistory.length === 0 ? (
                <p className="text-center text-muted">No email history yet</p>
              ) : (
                groupedEmailHistory.map((emailItem) => (
                  <div 
                    key={`${emailItem.id}_${emailItem.created_at}`} 
                    className="email-history-item"
                    style={{
                      display: "flex",
                      justifyContent: emailItem.type === "sent" ? "flex-end" : "flex-start",
                      width: "100%",
                      marginBottom: "10px"
                    }}
                  >
                    <div 
                      className={`opp-message-bubble ${emailItem.type === "sent" ? "sent" : "received"}`}
                      style={{
                        maxWidth: "80%",
                        padding: "10px 15px",
                        borderRadius: emailItem.type === "sent" ? "18px 18px 0 18px" : "18px 18px 18px 0",
                        backgroundColor: emailItem.type === "sent" ? "#DCF8C6" : "#FFFFFF",
                        boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                        border: "1px solid #eee"
                      }}
                    >
                      {emailItem.isGrouped && (
                        <div className="recipients">
                          <strong>Recipients:</strong> <b>{emailItem.recipients?.join(", ")}</b>
                        </div>
                      )}
                      <p className="email-subject">
                        <strong>Subject:</strong> {emailItem.subject || "No Subject"}
                      </p>
                      {!emailItem.isGrouped && emailItem.type === "sent" && (
                        <p className="email-receivers">
                          <strong>To:</strong> {emailItem.receiver_email}
                        </p>
                      )}
                      {!emailItem.isGrouped && emailItem.type === "received" && (
                        <p className="email-receivers">
                          <strong>From:</strong> {emailItem.sender_email}
                        </p>
                      )}
                      {emailItem.cc && (
                        <p className="email-cc">
                          <strong>CC:</strong> {emailItem.cc}
                        </p>
                      )}
                      <div
                        className="email-content"
                        dangerouslySetInnerHTML={{ __html: cleanEmailText(emailItem.text) }}
                      />
                      {emailItem.file_path && (
                        <a 
                          href={`${baseURL}${emailItem.file_path}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="attachment-link"
                          style={{
                            display: "inline-block",
                            marginTop: "5px",
                            color: "#007bff",
                            textDecoration: "none"
                          }}
                        >
                          📎 View Attachment
                        </a>
                      )}
                      <div 
                        className="timestamp" 
                        style={{
                          fontSize: "0.75rem",
                          color: "#666",
                          textAlign: emailItem.type === "sent" ? "right" : "left",
                          marginTop: "5px"
                        }}
                      >
                        {new Date(emailItem.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Send Email Form */}
            <div className="email-form">
              <h5>Send Email</h5>
              <div className="form-group mb-2">
                <label>To:</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Enter recipient emails (comma separated)"
                  value={receiverEmails}
                  onChange={(e) => setReceiverEmails(e.target.value)}
                />
              </div>

              <div className="cc-bcc-toggle mb-2">
                <Form.Check
                  type="checkbox"
                  label="Add CC"
                  checked={showCC}
                  onChange={() => setShowCC(!showCC)}
                  inline
                />
                <Form.Check
                  type="checkbox"
                  label="Add BCC"
                  checked={showBCC}
                  onChange={() => setShowBCC(!showBCC)}
                  inline
                />
              </div>

              {showCC && (
                <div className="form-group mb-2">
                  <label>CC:</label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter CC emails (comma separated)"
                    value={ccEmails}
                    onChange={(e) => setCcEmails(e.target.value)}
                  />
                </div>
              )}

              {showBCC && (
                <div className="form-group mb-2">
                  <label>BCC:</label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter BCC emails (comma separated)"
                    value={bccEmails}
                    onChange={(e) => setBccEmails(e.target.value)}
                  />
                </div>
              )}

              <div className="form-group mb-2">
                <label>Subject:</label>
                <Form.Control
                  type="text"
                  placeholder="Enter Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="form-group mb-2">
                <label>Message:</label>
                <ReactQuill
                  theme="snow"
                  value={text}
                  onChange={setText}
                  style={{ height: "150px", marginBottom: "50px" }}
                  placeholder="Enter your message here..."
                />
              </div>

              <div className="form-group mb-2">
                <label>Attachment:</label>
                <Form.Control 
                  type="file" 
                  onChange={handleFileChange} 
                />
              </div>

              <Button
                style={{ backgroundColor: "#ff9966", borderColor: "#ff9966" }}
                onClick={handleSendEmail}
                disabled={loading}
                className="mt-3"
              >
                {loading ? "Sending..." : "Send Email"}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailCommentModalPopemployee;